import AuthApi from '@apis/auth/AuthApi';
import {
    ACCESS_TOKEN_MAX_AGE_SECOND,
    REFRESH_TOKEN_MAX_AGE_SECOND,
} from '@constants/auth';
import useUser from '@hooks/auth/useUser';
import useOnGoogleSignIn from '@hooks/native/useOnGoogleSignIn';
import useToast from '@hooks/ui/useToast';
import useProject from '@hooks/useProject';
import useProjectId from '@hooks/useProjectId';
import { GoogleSignInError } from '@models/auth';
import type { RegisteredActivityName } from '@stackflow/config';
import { useFlow } from '@stackflow/react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

interface IFlow {
    activityName: RegisteredActivityName,
    activityParams: object,
}

function useSignInWithGoogle() {
    const projectId = useProjectId();
    const [cookies, setCookies, removeCookies] = useCookies(['accessToken', 'refreshToken', 'authFlow'], {
        doNotParse: true,
    });
    const project = useProject();
    const { getUserProfile } = useUser();
    const { replace, push, pop } = useFlow();
    const { t } = useTranslation('common');
    const toast = useToast();
    const { mutate } = useMutation({
        mutationFn: project?.serviceCountry === 'KR'
            ? AuthApi.oauthSIgnIn : AuthApi.oauthSignInWithEmail,
        retry: false,
        onSuccess(data) {
            const { token, refreshToken } = data;
            setCookies('accessToken', token, {
                path: '/',
                maxAge: ACCESS_TOKEN_MAX_AGE_SECOND,
            });
            setCookies('refreshToken', refreshToken, {
                path: '/',
                maxAge: REFRESH_TOKEN_MAX_AGE_SECOND,
            });
            getUserProfile();
            window.localStorage.setItem('lastLoginMethod', 'GOOGLE');
            try {
                const parsed: IFlow[] = JSON.parse(cookies.authFlow);
                pop({ animate: false });
                for (let i = 0; i < parsed.length; i += 1) {
                    const flow = parsed[i];
                    push(flow.activityName, flow.activityParams, { animate: i + 1 === parsed.length });
                }
                removeCookies('authFlow');
            } catch (e) {
                console.error(e);
                // replace('MainActivity', {});
                pop();
            }
        },
        onError(e: AxiosError<GoogleSignInError>) {
            if (e.response?.status === 401) {
                push('SignInActivity', { }, { animate: false });
                push('SignUpActivity', {
                    googleId: e.response?.data?.data?.googleId,
                    nickname: e.response?.data?.data?.nickname,
                    email: e.response?.data?.data?.email,
                });
            } else {
                console.error(e);
                toast.error(t('oauthSignInFailed'));
                pop({ animate: false });
                push('SignInActivity', { });
            }
        },
    });

    useOnGoogleSignIn({
        deps: [projectId, t],
        callbackFn: ({ isSuccess, idToken }) => {
            if (isSuccess) {
                mutate({ code: idToken!, projectId, signUpType: 'GOOGLE' });
            } else {
                toast.error(t('googleSignInFailed'));
            }
        },
    });
}

export default useSignInWithGoogle;
